<template>
  <div class="pt-6 pb-10">
    <div
      v-if="review"
      class="grid grid-cols-3 items-start gap-6"
      :class="{
        'rounded-lg border border-success pb-2': message?.inline,
      }"
    >
      <div class="col-span-3">
        <MessageBanner
          v-if="message"
          :headline="message.headline"
          :message="message.text"
          :type="message.type"
          :inline="message.inline"
          class="rounded-t-lg"
        >
          <template v-if="canSendReminder">
            <button
              v-if="product.configFlags.reviewRemindersAllowed"
              data-testid="send-reminder-button"
              class="btn-outline-neutral-xs"
              :disabled="reminderDisabled"
              @click="$emit('send-reminder')"
            >
              {{ t('Reviews.product.view.actions.sendReminder') }}
              <BellIcon class="lucide-icon-s" />
            </button>
            <TooltipWrapper v-else>
              <template #trigger>
                <button class="btn-outline-neutral-xs" data-testid="send-reminder-button" disabled>
                  <LockIcon class="lucide-icon-s" />
                  {{ t('Reviews.product.view.actions.sendReminder') }}
                </button>
              </template>
              <template #content>
                <div v-html="t('Reviews.product.action.sendReminder.unlockFeatureTooltip')" />
              </template>
            </TooltipWrapper>
          </template>
          <template v-if="hasOldReview && state === SurveyResponseState.Published" #action>
            <button
              v-if="product.configFlags.reviewRemindersAllowed"
              class="btn-outline-neutral-xs text-nowrap"
              data-testid="reviews-refresh-reminder"
              @click="$emit('send-reminder')"
            >
              {{ t('Reviews.product.view.actions.sendRefreshReminder') }}
            </button>
            <TooltipWrapper v-else>
              <template #trigger>
                <button class="btn-outline-neutral-xs text-nowrap" data-testid="reviews-refresh-reminder" disabled>
                  <LockIcon class="lucide-icon-s" />
                  {{ t('Reviews.product.view.actions.sendRefreshReminder') }}
                </button>
              </template>
              <template #content>
                <div v-html="t('Reviews.product.action.sendReminder.unlockFeatureTooltip')" />
              </template>
            </TooltipWrapper>
          </template>
        </MessageBanner>
      </div>
      <ReviewOverview class="col-span-3 px-4 desktop:col-span-1" :review="review" />
      <div class="col-span-3 px-4 desktop:col-span-2">
        <VideoReview v-if="review.video" class="col-span-2" :video-review="review.video" />
        <TextReviewQuotes
          v-else
          class="mb-10"
          :class="{
            'opacity-50': state === SurveyResponseState.ReDrafted,
          }"
          :review="review"
        />
        <ReplyItem
          v-if="!!reviewReply"
          :can-reply="product.configFlags.reviewRepliesAllowed"
          :disabled-hint="t('Reviews.product.review.sendReplyDisabledHint')"
          :entity-title="product.title"
          :review-reply="reviewReply"
          @delete-reply="$emit('delete-reply')"
          @edit-reply="$emit('edit-reply')"
        />
        <RemindersHistory
          v-if="surveyResponse.reminders.length > 0"
          :author-name="surveyResponse.reviewerFirstName || undefined"
          class="my-6"
          :reminders="surveyResponse.reminders"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MessageBanner, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { BellIcon, LockIcon } from '@ramp106/omrjs-core-ui/icons'
import { ReviewOverview, TextReviewQuotes, VideoReview } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReplyItem from '@/components/ReviewsCommon/ReviewReply/ReplyItem.vue'
import { SurveyResponseState, useSurveyResponseState } from '@/composables/useSurveyResponseState'
import { type ProductData, SurveyResponseRejectedReasons } from '@/gql/reviews'

import getMessageBannerForReviewState from './getMessageBannerForReviewState'
import RemindersHistory from './RemindersHistory.vue'
import type { SurveyResponseDetails } from './types'

const props = defineProps<{
  product: Pick<ProductData, 'id' | 'title' | 'configFlags'>
  surveyResponse: SurveyResponseDetails
  reminderDisabled?: boolean
  hasOldReview?: boolean
}>()

defineEmits(['send-reminder', 'delete-reply', 'edit-reply'])

const { t } = useI18n()

const state = useSurveyResponseState(props.surveyResponse)

const review = computed(() => {
  const response = props.surveyResponse
  let recommendationScore
  if (
    [SurveyResponseState.Published, SurveyResponseState.Proposed, SurveyResponseState.Rejected, SurveyResponseState.ReDrafted].includes(
      state.value,
    )
  ) {
    recommendationScore = response.review?.recommendationScore
  }

  return {
    positive: response.review?.positive,
    negative: response.review?.negative,
    problems: response.review?.problems,
    anonymous: response.anonymous,
    companyField: response.reviewerIndustry,
    companyName: response.reviewerCompany,
    companyPosition: response.reviewerPosition || response.review?.companyPosition,
    companySize: response.reviewerCompanySize,
    id: response.id,
    incentive: !!response.incentive,
    publishedAt: response.publishedAt || response.firstPublishedAt || response.createdAt,
    reviewerFirstName: response.review?.reviewerFirstName,
    reviewerLastName: response.review?.reviewerLastName,
    recommendationScore,
    title: response.review?.title,
    product: { title: response.review?.product?.title || props.product.title },
    video: response.review?.video,
    purposes: response.review?.purposes,
  }
})

const reviewReply = computed(() => props.surveyResponse.review?.reviewReply || null)

const message = computed(() => {
  return getMessageBannerForReviewState(
    state.value,
    props.surveyResponse.reviewerFirstName || t('Reviews.product.review.history.unknownAuthor'),
    props.surveyResponse.rejectedReason,
    canSendReminder.value,
    props.hasOldReview,
  )
})

const canSendReminder = computed(() => {
  const isRemindableState = [SurveyResponseState.Drafted, SurveyResponseState.ReDrafted, SurveyResponseState.Stale].includes(state.value)

  const isRemindableRejectedReason =
    state.value === SurveyResponseState.Rejected &&
    props.surveyResponse.rejectedReason &&
    [
      SurveyResponseRejectedReasons.TooThin,
      SurveyResponseRejectedReasons.InsufficientChanges,
      SurveyResponseRejectedReasons.IncorrectCompany,
    ].includes(props.surveyResponse.rejectedReason)

  return !props.reminderDisabled && (isRemindableState || isRemindableRejectedReason)
})
</script>
